/**
 * Codespace bootstrap fetch — wraps the `/api/codespace/bootstrap/:name` call
 * with an AbortController timeout, exponential-backoff retries, and structured
 * stderr events the agent-runner-handler can ingest.
 *
 * Used at agent startup (`cli.ts`) and for runtime taskToken refresh when the
 * server signals an auth rejection (`agent-connection.ts`).
 */
import { sleep } from "../utils/sleep.js";

export interface BootstrapConfig {
  mode?: "task" | "project";
  taskId?: string;
  sessionId?: string;
  taskToken?: string;
  runnerMode?: string;
  agentMode?: string;
  isAuto?: string;
  taskBranch?: string;
  projectId?: string;
  projectToken?: string;
  apiUrl?: string;
  workspaceBranch?: string;
  envVars?: Record<string, string>;
}

export interface BootstrapAttemptResult {
  ok: boolean;
  status?: number;
  body?: BootstrapConfig;
  errorText?: string;
  reason?: string;
}

export interface BootstrapFailureEvent {
  event: "bootstrap_failed";
  reason: string;
  apiUrl: string;
  instanceName: string;
  hasBootstrapToken: boolean;
  hasTaskToken: boolean;
  attempt: number;
  status?: number;
  detail?: string;
}

const BOOTSTRAP_TIMEOUT_MS = 30_000;
const RETRY_DELAYS_MS = [5_000, 10_000, 20_000];

function emitFailureEvent(payload: BootstrapFailureEvent): void {
  process.stderr.write(JSON.stringify(payload) + "\n");
}

async function singleBootstrapAttempt(
  apiUrl: string,
  instanceName: string,
  bootstrapToken: string | undefined,
  timeoutMs: number,
): Promise<BootstrapAttemptResult> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const headers: Record<string, string> = {};
    if (bootstrapToken) headers["x-codespace-token"] = bootstrapToken;
    const response = await fetch(`${apiUrl}/api/codespace/bootstrap/${instanceName}`, {
      headers,
      signal: controller.signal,
    });
    if (!response.ok) {
      const errorText = await response.text().catch(() => "");
      return {
        ok: false,
        status: response.status,
        errorText: errorText.slice(0, 500),
        reason: response.status === 401 || response.status === 403 ? "auth_rejected" : "http_error",
      };
    }
    const body = (await response.json()) as BootstrapConfig;
    return { ok: true, body };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    const reason = controller.signal.aborted ? "timeout" : "network_error";
    return { ok: false, errorText: message.slice(0, 500), reason };
  } finally {
    clearTimeout(timer);
  }
}

export interface FetchBootstrapOptions {
  apiUrl: string;
  instanceName: string;
  bootstrapToken: string | undefined;
  timeoutMs?: number;
  retryDelaysMs?: number[];
  /** Whether to consider any HTTP error retryable. Network/timeout always retry. */
  retryOnHttpError?: boolean;
}

export interface FetchBootstrapSuccess {
  ok: true;
  config: BootstrapConfig;
  attempts: number;
}

export interface FetchBootstrapFailure {
  ok: false;
  reason: string;
  status?: number;
  detail?: string;
  attempts: number;
}

function buildFailure(
  reason: string,
  attempts: number,
  status: number | undefined,
  detail: string | undefined,
): FetchBootstrapFailure {
  const out: FetchBootstrapFailure = { ok: false, reason, attempts };
  if (status === undefined) {
    // no status field
  } else {
    out.status = status;
  }
  if (detail) out.detail = detail;
  return out;
}

function isRetryable(reason: string, retryOnHttpError: boolean | undefined): boolean {
  if (reason === "timeout" || reason === "network_error") return true;
  return retryOnHttpError === true && reason === "http_error";
}

/**
 * Fetch the bootstrap payload with retries. Network errors and timeouts always
 * trigger a retry; HTTP errors are retried only when `retryOnHttpError` is set
 * (off by default — a 401/403/404 won't recover from retries).
 */
export async function fetchBootstrap(
  opts: FetchBootstrapOptions,
): Promise<FetchBootstrapSuccess | FetchBootstrapFailure> {
  const timeoutMs = opts.timeoutMs ?? BOOTSTRAP_TIMEOUT_MS;
  const delays = opts.retryDelaysMs ?? RETRY_DELAYS_MS;
  const maxAttempts = delays.length + 1;
  const hasBootstrapToken = Boolean(opts.bootstrapToken);
  const hasTaskToken = Boolean(process.env.CONVEYOR_TASK_TOKEN);

  let lastReason = "unknown";
  let lastStatus: number | undefined;
  let lastDetail: string | undefined;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const result = await singleBootstrapAttempt(
      opts.apiUrl,
      opts.instanceName,
      opts.bootstrapToken,
      timeoutMs,
    );
    if (result.ok && result.body) {
      return { ok: true, config: result.body, attempts: attempt };
    }
    lastReason = result.reason ?? "unknown";
    lastStatus = result.status;
    lastDetail = result.errorText;

    const failurePayload: BootstrapFailureEvent = {
      event: "bootstrap_failed",
      reason: lastReason,
      apiUrl: opts.apiUrl,
      instanceName: opts.instanceName,
      hasBootstrapToken,
      hasTaskToken,
      attempt,
    };
    if (lastStatus === undefined) {
      // no status field
    } else {
      failurePayload.status = lastStatus;
    }
    if (lastDetail) failurePayload.detail = lastDetail;
    emitFailureEvent(failurePayload);

    if (!isRetryable(lastReason, opts.retryOnHttpError) || attempt >= maxAttempts) {
      return buildFailure(lastReason, attempt, lastStatus, lastDetail);
    }
    await sleep(delays[attempt - 1]);
  }

  return buildFailure(lastReason, maxAttempts, lastStatus, lastDetail);
}

/** Apply a successful bootstrap config to process.env (mutates env vars). */
export function applyBootstrapToEnv(config: BootstrapConfig): void {
  for (const [key, value] of Object.entries(config.envVars ?? {})) {
    process.env[key] = value;
  }
  if (config.mode === "project") {
    if (config.projectToken) process.env.CONVEYOR_PROJECT_TOKEN = config.projectToken;
    if (config.projectId) process.env.CONVEYOR_PROJECT_ID = config.projectId;
    if (config.workspaceBranch) process.env.CONVEYOR_WORKSPACE_BRANCH = config.workspaceBranch;
    return;
  }
  if (config.taskId) process.env.CONVEYOR_TASK_ID = config.taskId;
  if (config.sessionId) process.env.CONVEYOR_SESSION_ID = config.sessionId;
  if (config.taskToken) process.env.CONVEYOR_TASK_TOKEN = config.taskToken;
  if (config.agentMode !== undefined) process.env.CONVEYOR_AGENT_MODE = config.agentMode;
  if (config.isAuto !== undefined) process.env.CONVEYOR_IS_AUTO = config.isAuto;
  if (config.runnerMode) process.env.CONVEYOR_MODE = config.runnerMode;
  if (config.taskBranch) process.env.CONVEYOR_TASK_BRANCH = config.taskBranch;
}
